/**
 * @fileoverview View/Edit component for settings sections
 * Provides inline editing within a Paper container, following DmsDetailModal pattern
 */
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { intl, DmsButton } from "dmsbasereact";
import { Box, Paper, Typography, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Swal from "sweetalert2";

/**
 * Settings view/edit component for inline editing of settings sections
 *
 * @example
 * <SettingsViewEdit
 *   data={settings}
 *   loading={loading}
 *   title="General Settings"
 *   description="Configure general application settings"
 *   viewContent={SettingsViewContent}
 *   editContent={SettingsEditContent}
 *   prepareFormData={(data) => ({ theme: data.theme, ... })}
 *   buildPayload={(formData) => ({ theme: formData.theme, ... })}
 *   onSave={handleSave}
 *   onDataUpdated={refreshSettings}
 *   canEdit={true}
 * />
 */
export const SettingsViewEdit = ({
  data = null,
  loading: externalLoading = false,
  title,
  description = null,
  viewContent: ViewContent,
  editContent: EditContent,
  prepareFormData,
  buildPayload = null,
  onSave,
  onDataUpdated = null,
  confirmTitle = null,
  confirmMessage = null,
  editContentProps = {},
  viewContentProps = {},
  canEdit = true,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Local data that can be updated after save
  const [localData, setLocalData] = useState(null);

  // Update local data when prop changes
  useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  const handleEnterEditMode = useCallback(() => {
    if (!localData || !prepareFormData) return;
    const prepared = prepareFormData(localData);
    setFormData(prepared);
    setIsEditMode(true);
  }, [localData, prepareFormData]);

  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
    setFormData({});
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;

    // Confirmation dialog before save
    const result = await Swal.fire({
      title: confirmTitle || intl.get("ConfirmSave").d("Confirm Save"),
      text: confirmMessage || intl.get("ConfirmSaveMessage").d("Are you sure you want to save the changes?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.get("Confirm").d("Confirm"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    const payload = buildPayload ? buildPayload(formData, localData) : formData;
    const updatedData = await onSave(payload);
    setSaving(false);

    if (updatedData) {
      // Update local state with new data
      setLocalData(updatedData);
      setIsEditMode(false);
      setFormData({});
      // Notify parent to refresh
      onDataUpdated?.();
    }
  }, [localData, formData, onSave, onDataUpdated, buildPayload, confirmTitle, confirmMessage]);

  const isLoading = externalLoading;
  const showEditButton = canEdit !== false && !isEditMode && !isLoading && localData;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {showEditButton && (
          <DmsButton
            icon={<EditIcon />}
            text={intl.get("Edit").d("Edit")}
            onClick={handleEnterEditMode}
          />
        )}
      </Box>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        localData && (
          <>
            {isEditMode ? (
              <>
                <EditContent
                  data={localData}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                  {...editContentProps}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                  <DmsButton
                    icon={<CancelIcon />}
                    text={intl.get("Cancel").d("Cancel")}
                    onClick={handleCancelEditMode}
                    disabled={saving}
                  />
                  <DmsButton
                    icon={<SaveIcon />}
                    text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                    onClick={handleSave}
                    disabled={saving}
                  />
                </Box>
              </>
            ) : (
              <ViewContent
                data={localData}
                {...viewContentProps}
              />
            )}
          </>
        )
      )}
    </Paper>
  );
};

SettingsViewEdit.propTypes = {
  /** Settings data object */
  data: PropTypes.object,
  /** External loading state */
  loading: PropTypes.bool,
  /** Section title */
  title: PropTypes.string.isRequired,
  /** Section description */
  description: PropTypes.string,
  /** Component to render in view mode - receives { data, ...viewContentProps } */
  viewContent: PropTypes.elementType.isRequired,
  /** Component to render in edit mode - receives { data, formData, onFieldChange, ...editContentProps } */
  editContent: PropTypes.elementType.isRequired,
  /** Function to prepare form data from settings data */
  prepareFormData: PropTypes.func.isRequired,
  /** Function to build payload from form data for save */
  buildPayload: PropTypes.func,
  /** Save handler - receives (payload), should return updated data or null */
  onSave: PropTypes.func.isRequired,
  /** Callback when data is successfully updated */
  onDataUpdated: PropTypes.func,
  /** Custom confirmation dialog title */
  confirmTitle: PropTypes.string,
  /** Custom confirmation dialog message */
  confirmMessage: PropTypes.string,
  /** Additional props to pass to edit content component */
  editContentProps: PropTypes.object,
  /** Additional props to pass to view content component */
  viewContentProps: PropTypes.object,
  /** Whether edit button should be shown (default: true) */
  canEdit: PropTypes.bool,
};


export default SettingsViewEdit;
